import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Form } from "react-bootstrap";
import { Search, X, User } from "lucide-react";
import RulesService from "../../../_services/RulesService";

interface ReviewerUser {
  id: number;
  name: string | null;
  email: string | null;
}

interface Props {
  value: number[];
  onChange: (next: number[]) => void;
}

interface Anchor {
  top: number;
  left: number;
  width: number;
}

const PANEL_MAX_HEIGHT = 280;

/**
 * Typeahead reviewer picker. Chips for already-selected reviewers (with
 * name + email if known), search box that queries /admin/rules/users/search,
 * portaled dropdown so it escapes the modal's overflow chrome.
 *
 * On mount with a non-empty value, hydrates the chips by IDs so saved
 * reviewers render with their display info.
 */
const ReviewerPicker: React.FC<Props> = ({ value, onChange }) => {
  const [chips, setChips] = useState<ReviewerUser[]>([]);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ReviewerUser[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Hydrate chips from incoming IDs that we haven't displayed yet.
  useEffect(() => {
    const needHydrate = value.filter((id) => !chips.find((c) => c.id === id));
    if (needHydrate.length === 0) {
      // Trim chips for IDs that are no longer in value
      setChips((prev) => prev.filter((c) => value.includes(c.id)));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res: any = await RulesService.hydrateReviewerUsers(needHydrate);
        const users = (res?.data?.data?.users || []) as ReviewerUser[];
        if (cancelled) return;
        setChips((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          const merged = [...prev, ...users.filter((u) => !seen.has(u.id))];
          // Filter to current value order
          return value
            .map((id) => merged.find((m) => m.id === id) || { id, name: null, email: null })
            .filter(Boolean) as ReviewerUser[];
        });
      } catch {
        // Best-effort hydration; still render bare IDs as fallback chips.
        if (!cancelled) {
          setChips((prev) => {
            const have = new Set(prev.map((c) => c.id));
            const bare = needHydrate.filter((id) => !have.has(id)).map((id) => ({ id, name: null, email: null }));
            return [...prev, ...bare];
          });
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open || debounced.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    RulesService.searchReviewerUsers(debounced)
      .then((res: any) => {
        if (cancelled) return;
        setResults((res?.data?.data?.users || []) as ReviewerUser[]);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setErr(e?.response?.data?.error?.message || e?.message || "Search failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [debounced, open]);

  const updateAnchor = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const wantedTop = r.bottom + 6;
    const spaceBelow = viewportH - r.bottom;
    const top = spaceBelow < PANEL_MAX_HEIGHT + 20 && r.top > PANEL_MAX_HEIGHT
      ? r.top - PANEL_MAX_HEIGHT - 6
      : wantedTop;
    setAnchor({
      top,
      left: Math.max(12, Math.min(r.left, viewportW - r.width - 12)),
      width: Math.max(280, r.width),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateAnchor();
    const handler = () => updateAnchor();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open]);

  // Outside-click closes the panel
  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || wrapRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onMouse);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouse);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const addReviewer = (u: ReviewerUser) => {
    if (value.includes(u.id)) return;
    onChange([...value, u.id]);
    setQuery("");
    inputRef.current?.focus();
  };

  const removeReviewer = (id: number) => {
    onChange(value.filter((x) => x !== id));
  };

  return (
    <div className="bm-reviewer-picker" ref={wrapRef}>
      <div className="bm-reviewer-chips">
        {chips.map((c) => (
          <span key={c.id} className="bm-reviewer-chip" title={c.email || `User #${c.id}`}>
            <User size={11} />
            <span className="bm-reviewer-chip-label">
              {c.name || `User #${c.id}`}
            </span>
            <button
              type="button"
              className="bm-reviewer-chip-remove"
              onClick={() => removeReviewer(c.id)}
              aria-label="Remove reviewer"
            >
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="bm-reviewer-input-wrap">
        <Search size={13} className="bm-reviewer-search-icon" />
        <Form.Control
          ref={inputRef as any}
          size="sm"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); updateAnchor(); }}
          onFocus={() => { setOpen(true); updateAnchor(); }}
          placeholder="Search by name or email…"
        />
      </div>
      <Form.Text className="text-muted">
        Only the chosen reviewers see this rule's pending eligibility queue. Leave empty to allow any credential-admin user to approve.
      </Form.Text>

      {open && anchor && createPortal(
        <div
          ref={panelRef}
          className="bm-reviewer-panel"
          style={{
            position: "fixed",
            top: anchor.top,
            left: anchor.left,
            width: anchor.width,
            maxHeight: PANEL_MAX_HEIGHT,
          }}
        >
          {err && <div className="bm-reviewer-state error">{err}</div>}
          {!err && debounced.length < 2 && (
            <div className="bm-reviewer-state">Type at least 2 characters…</div>
          )}
          {!err && debounced.length >= 2 && loading && (
            <div className="bm-reviewer-state">Searching…</div>
          )}
          {!err && debounced.length >= 2 && !loading && results.length === 0 && (
            <div className="bm-reviewer-state">No matches.</div>
          )}
          {!err && results.map((u) => {
            const already = value.includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                className={`bm-reviewer-row ${already ? "is-already" : ""}`}
                disabled={already}
                onClick={() => addReviewer(u)}
              >
                <span className="bm-reviewer-row-icon"><User size={14} /></span>
                <span className="bm-reviewer-row-meta">
                  <span className="bm-reviewer-row-name">{u.name || `User #${u.id}`}</span>
                  {u.email && <span className="bm-reviewer-row-email">{u.email}</span>}
                </span>
                {already && <span className="bm-reviewer-row-already">added</span>}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ReviewerPicker;
